import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

export function MeteorDodge({ onScore }) {
  const [ship, setShip] = useState(50);
  const [rocks, setRocks] = useState([]);
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const raf = useRef(null);
  const shipRef = useRef(50);
  const idRef = useRef(0);
  const reported = useRef(false);

  const start = () => { setShip(50); shipRef.current = 50; setRocks([]); setScore(0); setRunning(true); setOver(false); reported.current = false; };

  useEffect(() => {
    if (!running) return;
    let frame = 0, local = [], sc = 0;
    const loop = () => {
      frame++;
      if (frame % 28 === 0) local.push({ id: idRef.current++, x: 6 + Math.random() * 88, y: 0 });
      local = local.map((r) => ({ ...r, y: r.y + 1.8 })).filter((r) => r.y < 105);
      setRocks([...local]);
      for (const r of local) {
        if (r.y > 84 && r.y < 96 && Math.abs(r.x - shipRef.current) < 9) { setRunning(false); setOver(true); setScore(sc); return; }
      }
      sc++; if (sc % 3 === 0) setScore(Math.floor(sc / 3));
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    const key = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a") shipRef.current = Math.max(6, shipRef.current - 6);
      if (e.key === "ArrowRight" || e.key === "d") shipRef.current = Math.min(94, shipRef.current + 6);
      setShip(shipRef.current);
    };
    window.addEventListener("keydown", key);
    return () => { cancelAnimationFrame(raf.current); window.removeEventListener("keydown", key); };
  }, [running]);

  const move = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) / rect.width * 100;
    shipRef.current = Math.max(6, Math.min(94, x)); setShip(shipRef.current);
  };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 20); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="meteor-dodge">
      <p className="font-mono text-neon-cyan text-lg mb-3" data-testid="meteor-score">SCORE: {score}</p>
      <div onMouseMove={move} onTouchMove={move} className="relative bg-zinc-950 border-2 border-zinc-700 rounded-xl overflow-hidden" style={{ width: "min(92vw, 360px)", height: 360, touchAction: "none" }}>
        {rocks.map((r) => (
          <div key={r.id} className="absolute text-2xl" style={{ left: `${r.x}%`, top: `${r.y}%`, transform: "translate(-50%,-50%)" }}>☄️</div>
        ))}
        <div className="absolute text-3xl" style={{ left: `${ship}%`, bottom: 6, transform: "translateX(-50%)" }}>🚀</div>
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="meteor-over">Boom! Score {score}</p>}
            <Button onClick={start} data-testid="meteor-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Move (mouse / ← → / A D) to dodge the meteors. Survive!</p>
    </div>
  );
}
